import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, cacheGet, queueCompletion, useOnline } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { LESSON_IMAGES, TRACK_IMAGES, HERO_IMG } from "@/lib/images";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2 } from "lucide-react";

export default function Lesson() {
  const { id, lid } = useParams();
  const track = cacheGet(`track_${id}`);
  const [answers, setAnswers] = useState({});
  const [marked, setMarked] = useState(false);
  const { t, pick } = useLang();
  const online = useOnline();
  const navigate = useNavigate();

  if (!track) return <p className="text-slate-500">Loading… <Link to={`/track/${id}`} className="text-teal-700">Back</Link></p>;
  const lesson = track.lessons.find((l) => l.id === lid);
  if (!lesson) return <p className="text-slate-500">Not found</p>;

  const markComplete = async () => {
    setMarked(true);
    try {
      await api.post(`/tracks/${id}/lessons/${lid}/complete`);
      toast.success(t("completed"));
    } catch (e) {
      queueCompletion(id, lid);
      toast.info(t("syncPending"));
    }
    navigate(`/track/${id}`);
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <Link to={`/track/${id}`} data-testid="back-to-track-link" className="inline-flex items-center gap-1 text-sm text-teal-700 font-medium"><ArrowLeft size={16} /> {pick(track.title)}</Link>
      <div className="relative rounded-2xl overflow-hidden h-40 sm:h-52" data-testid="lesson-image-header">
        <img src={LESSON_IMAGES[lid] || TRACK_IMAGES[id] || HERO_IMG} alt={pick(lesson.title)} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-teal-950/80 via-transparent to-transparent" />
        <h1 className="absolute bottom-3 left-4 right-4 font-heading text-xl sm:text-3xl font-extrabold text-white leading-snug">{pick(lesson.title)}</h1>
      </div>
      <article data-testid="lesson-content" className="bg-white rounded-2xl border border-slate-200 p-5 text-slate-700 leading-relaxed space-y-4">
        {pick(lesson.content).split("\n\n").map((p, i) => <p key={i}>{p}</p>)}
      </article>

      {lesson.quiz.length > 0 && (
      <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-5">
        <h2 className="font-heading font-bold text-lg text-slate-900">{t("quickCheck")}</h2>
        {lesson.quiz.map((qz, qi) => {
          const chosen = answers[qi];
          return (
            <div key={qi} className="space-y-2">
              <p className="font-medium text-slate-800">{qi + 1}. {pick(qz.q)}</p>
              <div className="grid gap-2">
                {pick(qz.options).map((opt, oi) => {
                  let cls = "border-slate-200 bg-white hover:border-teal-300";
                  if (chosen !== undefined) {
                    if (oi === qz.answer) cls = "border-emerald-400 bg-emerald-50 text-emerald-800";
                    else if (oi === chosen) cls = "border-red-300 bg-red-50 text-red-700";
                    else cls = "border-slate-100 text-slate-400";
                  }
                  return (
                    <button key={oi} data-testid={`quiz-q${qi}-opt${oi}`} disabled={chosen !== undefined}
                      onClick={() => setAnswers({ ...answers, [qi]: oi })}
                      className={`text-left px-4 py-3 rounded-xl border font-medium text-sm transition-colors ${cls}`}>
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </section>
      )}

      <button data-testid="mark-lesson-complete-btn" onClick={markComplete} disabled={marked}
        className="w-full sm:w-auto h-12 px-8 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2">
        <CheckCircle2 size={18} /> {t("markComplete")}
      </button>
      {!online && <p className="text-xs text-amber-600">{t("syncPending")}</p>}
    </div>
  );
}
